import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaCcMastercard, FaCcVisa, FaCheckCircle, FaCreditCard, FaMapMarkerAlt, FaTimesCircle } from 'react-icons/fa';
import { GiTennisRacket } from 'react-icons/gi';
import { toast } from 'react-toastify';
import { initiateGeneralPayment } from '../../../api/paymentService';
import { getPublicTennisAcademyTariffs, initiateTennisAcademyPayment, checkTennisAcademyMembership, initiateTennisAcademyRenewal } from '../../../api/tennisAcademyService';
import './TennisAcademyApplicationForm.scss';

const TENNIS_FACILITIES = [
  { id: 'toki-millet-parki', name: 'TOKİ Millet Parkı' },
  { id: 'rauf-denktas', name: 'Rauf Denktaş' },
  { id: 'sahil-spor-kompleksi', name: 'Sahil Spor Kompleksi' }
];

const initialFormData = {
  tariffId: '',
  facilityName: '',
  firstName: '',
  lastName: '',
  identityNumber: '',
  phone: '',
  email: '',
  userGender: '',
  birthDate: '',

};

const initialPaymentData = {
  cardNumber: '',
  cardName: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: ''
};

const TennisAcademyApplicationForm = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [paymentData, setPaymentData] = useState(initialPaymentData);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentHtml, setPaymentHtml] = useState(null);
  const [paymentOutcome, setPaymentOutcome] = useState(null);
  const [resultMessage, setResultMessage] = useState('');
  const [resetKey, setResetKey] = useState(0);

  // Renewal State
  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'renewal'
  const [renewalIdentity, setRenewalIdentity] = useState('');
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [checkingMembership, setCheckingMembership] = useState(false);

  // Agreements State
  const [agreements, setAgreements] = useState({
    terms: false,
    refund: false,
    contract: false
  });

  const iframeRef = useRef(null);

  const selectedTariff = useMemo(
    () => tariffs.find((tariff) => String(tariff.tariffId) === String(formData.tariffId)) || null,
    [tariffs, formData.tariffId]
  );

  useEffect(() => {
    const loadTariffs = async () => {
      const res = await getPublicTennisAcademyTariffs();
      if (res?.isSuccess) {
        setTariffs(res.data || []);
      } else {
        toast.error(res?.resultMessage || 'Tarifeler alinamadi.');
      }
    };

    loadTariffs();
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'PAYMENT_RESULT') return;

      if (data.status === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Tenis akademi başvurunuz başarıyla alındı.');
        setPaymentHtml(null);
      } else if (data.status === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const href = iframe.contentWindow?.location?.href;
      if (!href) return;

      const iframeUrl = new URL(href);
      const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
      const error = iframeUrl.searchParams.get('error');

      if (paymentStatus === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Tenis akademi başvurunuz başarıyla alındı.');
        setPaymentHtml(null);
      } else if (paymentStatus === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    } catch {
      // Cross-origin on bank page.
    }
  };

  const luhnCheck = (value) => {
    let checksum = 0;
    let multiplier = 1;

    for (let i = value.length - 1; i >= 0; i -= 1) {
      let product = Number(value.charAt(i)) * multiplier;
      if (product > 9) {
        checksum += 1;
        product -= 10;
      }
      checksum += product;
      multiplier = multiplier === 1 ? 2 : 1;
    }

    return checksum % 10 === 0;
  };

  const detectCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return '1';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
    return null;
  };

  const validateStepOne = () => {
    if (!formData.tariffId) {
      toast.error('Lütfen bir tarife seçiniz.');
      return false;
    }

    if (!formData.facilityName) {
      toast.error('Lütfen bir tesis seçiniz.');
      return false;
    }

    if (!formData.firstName || !formData.lastName || !formData.identityNumber || !formData.phone || !formData.email) {
      toast.error('Lütfen zorunlu alanları doldurunuz.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Lütfen geçerli bir e-posta adresi giriniz.');
      return false;
    }

    if (formData.identityNumber.length !== 11) {
      toast.error('TC Kimlik No 11 haneli olmalıdır.');
      return false;
    }

    if (formData.phone.length < 10) {
      toast.error('Telefon numarası eksik görünüyor.');
      return false;
    }

    return true;
  };

  const handleContinue = () => {
    if (!validateStepOne()) return;
    setStep(2);
  };

  const handlePayment = async () => {
    setLoading(true);

    const errors = {};
    const pan = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (!pan) {
      errors.cardNumber = 'Kart numarası gereklidir.';
    } else if (pan.length !== 16) {
      errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    } else if (!luhnCheck(pan)) {
      errors.cardNumber = 'Geçersiz kart numarası.';
    }

    if (!paymentData.cardName.trim()) {
      errors.cardName = 'Kart üzerindeki isim gereklidir.';
    }

    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.expiry = 'Son kullanma tarihi seçiniz.';
    } else {
      const expYear = parseInt(paymentData.expiryYear, 10);
      const expMonth = parseInt(paymentData.expiryMonth, 10);
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        errors.expiry = 'Kartın son kullanma tarihi geçmiş.';
      }
    }

    if (!paymentData.cvv || paymentData.cvv.length !== 3) {
      errors.cvv = 'CVV 3 haneli olmalıdır.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      setLoading(false);
      return;
    }

    if (!agreements.terms || !agreements.refund || !agreements.contract) {
      toast.warn('Lütfen ilgili sözleşmeleri okuyup onaylayınız.');
      setLoading(false);
      return;
    }

    setPaymentErrors({});

    try {
      const initResponse = await initiateTennisAcademyPayment({
        tariffId: Number(formData.tariffId),
        facilityName: formData.facilityName || null,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        identityNumber: formData.identityNumber.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || null,
        userGender: formData.userGender || null,
        birthDate: formData.birthDate || null,
        notes: null
      });

      if (!initResponse?.isSuccess || !initResponse?.data) {
        toast.error(initResponse?.resultMessage || 'Başvuru başlatılamadı.');
        return;
      }

      const paymentResponse = await initiateGeneralPayment({
        amount: initResponse.data.amount,
        paymentGroup: 'TennisAcademyFee',
        referenceId: String(initResponse.data.applicationId),
        applicationId: initResponse.data.applicationId,
        description: initResponse.data.description || `Tenis Akademi Ücreti - ${initResponse.data.tariffName || ''}`,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber) || '1',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || ''
      });

      if (!paymentResponse?.isSuccess || !paymentResponse?.data?.html) {
        toast.error(paymentResponse?.resultMessage || 'Ödeme başlatılamadı.');
        return;
      }

      setPaymentHtml(paymentResponse.data.html);
      setStep(3);
    } catch (error) {
      console.error('Tenis akademi ödeme hatası:', error);
      toast.error('Ödeme işlemi sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewalSearch = async () => {
    if (!renewalIdentity || renewalIdentity.length !== 11) {
      toast.error('Geçerli bir TC Kimlik Numarası giriniz (11 hane).');
      return;
    }
    setCheckingMembership(true);
    setMembershipInfo(null);
    try {
      const res = await checkTennisAcademyMembership(renewalIdentity);
      if (res?.isSuccess && res.data) {
        setMembershipInfo(res.data);
      } else {
        toast.error(res?.resultMessage || 'Üyelik bulunamadı.');
      }
    } catch (err) {
      toast.error('Sorgulama sırasında bir hata oluştu.');
    } finally {
      setCheckingMembership(false);
    }
  };

  const handleRenewalPayment = async () => {
    if (!membershipInfo) return;
    setLoading(true);

    const errors = {};
    const pan = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (!pan) {
      errors.cardNumber = 'Kart numarası gereklidir.';
    } else if (pan.length !== 16) {
      errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    } else if (!luhnCheck(pan)) {
      errors.cardNumber = 'Geçersiz kart numarası.';
    }

    if (!paymentData.cardName.trim()) {
      errors.cardName = 'Kart üzerindeki isim gereklidir.';
    }

    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.expiry = 'Son kullanma tarihi seçiniz.';
    } else {
      const expYear = parseInt(paymentData.expiryYear, 10);
      const expMonth = parseInt(paymentData.expiryMonth, 10);
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        errors.expiry = 'Kartın son kullanma tarihi geçmiş.';
      }
    }

    if (!paymentData.cvv || paymentData.cvv.length !== 3) {
      errors.cvv = 'CVV 3 haneli olmalıdır.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      setLoading(false);
      return;
    }

    // Yenileme ekranında sözleşme onay kutuları kaldırıldı
    // if (!agreements.terms || !agreements.refund || !agreements.contract) {
    //   toast.warn('Lütfen ilgili sözleşmeleri okuyup onaylayınız.');
    //   setLoading(false);
    //   return;
    // }

    setPaymentErrors({});

    try {
      const initResponse = await initiateTennisAcademyRenewal({
        identityNumber: renewalIdentity.trim()
      });

      if (!initResponse?.isSuccess || !initResponse?.data) {
        toast.error(initResponse?.resultMessage || 'Yenileme ödemesi başlatılamadı.');
        return;
      }

      const paymentResponse = await initiateGeneralPayment({
        amount: initResponse.data.amount,
        paymentGroup: 'TennisAcademyFee',
        referenceId: String(initResponse.data.applicationId),
        applicationId: initResponse.data.applicationId,
        description: initResponse.data.description || `Tenis Akademi Yenileme - ${initResponse.data.tariffName || ''}`,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber) || '1',
        firstName: membershipInfo.maskedName.split(' ')[0] || 'AD',
        lastName: membershipInfo.maskedName.split(' ')[1] || 'SOYAD',
        email: ''
      });

      if (!paymentResponse?.isSuccess || !paymentResponse?.data?.html) {
        toast.error(paymentResponse?.resultMessage || 'Ödeme başlatılamadı.');
        return;
      }

      setPaymentHtml(paymentResponse.data.html);
      setStep(3);
    } catch (error) {
      console.error('Tenis akademi yenileme ödeme hatası:', error);
      toast.error('Ödeme işlemi sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData(initialFormData);
    setPaymentData(initialPaymentData);
    setPaymentErrors({});
    setPaymentHtml(null);
    setPaymentOutcome(null);
    setResultMessage('');
    setMembershipInfo(null);
    setRenewalIdentity('');
    setActiveTab('new');
    setResetKey(prev => prev + 1);
  };

  const renderActiveStepButton = () => {
     if (activeTab === 'new') return handlePayment;
     if (activeTab === 'renewal') return handleRenewalPayment;
     return handlePayment;
  };

  const previewCardNumber = paymentData.cardNumber || '0000 0000 0000 0000';
  const fallbackCardHolder = `${formData.firstName} ${formData.lastName}`.trim();
  const previewCardHolder = paymentData.cardName || fallbackCardHolder || 'AD SOYAD';
  const previewExpiry = paymentData.expiryMonth && paymentData.expiryYear
    ? `${paymentData.expiryMonth}/${String(paymentData.expiryYear).slice(-2)}`
    : 'AA/YY';
  return (
    <div className="sa-panel-inner sa-panel-inner--wide sa-tennis-application sa-unified-mode">
      <div className="sa-facility-details">
        <div className="sa-facility-details-header sa-unified-tab-header">
          <div className="sa-unified-tab-header-row">
            <h3 className="sa-unified-tab-title">
              <div className="sa-unified-tab-icon">
                <GiTennisRacket size={20} />
              </div>
              Tenis Akademisi
            </h3>
            <div className="sa-tennis-tabs" style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
              <button 
                type="button"
                className={`sa-tennis-tab-btn ${activeTab === 'new' ? 'active' : ''}`}
                onClick={() => { setActiveTab('new'); setStep(1); setPaymentOutcome(null); setPaymentHtml(null); }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: activeTab === 'new' ? '#0f75bc' : '#fff', color: activeTab === 'new' ? '#fff' : '#475569', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' }}
              >
                Yeni Başvuru
              </button>
              <button 
                type="button"
                className={`sa-tennis-tab-btn ${activeTab === 'renewal' ? 'active' : ''}`}
                onClick={() => { setActiveTab('renewal'); setStep(1); setPaymentOutcome(null); setPaymentHtml(null); }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: activeTab === 'renewal' ? '#0f75bc' : '#fff', color: activeTab === 'renewal' ? '#fff' : '#475569', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' }}
              >
                Üyelik Yenileme
              </button>
            </div>
          </div>
        </div>

        <div className="sa-facility-details-content" style={{ marginTop: '0' }}>
          {step === 1 ? (
             activeTab === 'new' ? (
          <div>
            <div className="sa-tennis-intro-banner">
              <div className="sa-intro-content">
                <h4>Hoş Geldiniz</h4>
                <p>Tenis Akademi başvurunuzu yapmak için size en uygun tarifeyi seçin ve kayıt bilgilerinizi doldurun.</p>
              </div>
            </div>
            <div className="sa-appointment-section sa-tennis-section">
              <div className="sa-booking-form sa-tennis-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                  Başvuru Bilgileri
                </h4>
                <div className="sa-form-grid">
                  <div className="sa-form-group">
                    <label>Paket <span className="sa-required">*</span></label>
                    <select className="sa-form-input" value={formData.tariffId} onChange={(event) => setFormData((prev) => ({ ...prev, tariffId: event.target.value }))}>
                      <option value="">Paket seçiniz</option>
                      {tariffs.map((tariff) => (
                        <option key={tariff.tariffId} value={tariff.tariffId}>
                          {tariff.tariffName} - {Number(tariff.amount || 0).toFixed(2)} {tariff.currency || 'TRY'}
                        </option>
                      ))}
                    </select>
                    {tariffs.length === 0 && <small className="sa-tennis-empty-tariff">Aktif paket bulunamadı.</small>}
                  </div>
                  <div className="sa-form-group sa-tennis-full">
                    <label>Tesis <span className="sa-required">*</span></label>
                    <div className="sa-tennis-facility-selector">
                      {TENNIS_FACILITIES.map((facility) => (
                        <button
                          key={facility.id}
                          type="button"
                          className={`sa-tennis-facility-btn${formData.facilityName === facility.name ? ' active' : ''}`}
                          onClick={() => setFormData((prev) => ({ ...prev, facilityName: facility.name }))}
                        >
                          <FaMapMarkerAlt className="sa-tennis-facility-icon" />
                          <span>{facility.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sa-form-group">
                    <label>Ad <span className="sa-required">*</span></label>
                    <input className="sa-form-input" value={formData.firstName} onChange={(event) => setFormData((prev) => ({ ...prev, firstName: event.target.value }))} />
                  </div>
                  <div className="sa-form-group">
                    <label>Soyad <span className="sa-required">*</span></label>
                    <input className="sa-form-input" value={formData.lastName} onChange={(event) => setFormData((prev) => ({ ...prev, lastName: event.target.value }))} />
                  </div>
                  <div className="sa-form-group">
                    <label>TC Kimlik No <span className="sa-required">*</span></label>
                    <input
                      className="sa-form-input"
                      maxLength={11}
                      value={formData.identityNumber}
                      onChange={(event) => setFormData((prev) => ({ ...prev, identityNumber: event.target.value.replace(/\D/g, '').slice(0, 11) }))}
                    />
                  </div>
                  <div className="sa-form-group">
                    <label>Telefon <span className="sa-required">*</span></label>
                    <input
                      className="sa-form-input"
                      value={formData.phone}
                      onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value.replace(/\D/g, '').slice(0, 11) }))}
                    />
                  </div>
                  <div className="sa-form-group">
                    <label>E-posta <span className="sa-required">*</span></label>
                    <input className="sa-form-input" type="email" value={formData.email} onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))} required />
                  </div>
                  <div className="sa-form-group">
                    <label>Cinsiyet</label>
                    <select className="sa-form-input" value={formData.userGender} onChange={(event) => setFormData((prev) => ({ ...prev, userGender: event.target.value }))}>
                      <option value="">Seçiniz</option>
                      <option value="Male">Erkek</option>
                      <option value="Female">Kadın</option>
                    </select>
                  </div>
                  <div className="sa-form-group">
                    <label>Doğum Tarihi</label>
                    <input key={`dob-${resetKey}`} className="sa-form-input" type="date" defaultValue={formData.birthDate} max="9999-12-31" onChange={(event) => setFormData((prev) => ({ ...prev, birthDate: event.target.value }))} />
                  </div>

                </div>

                {selectedTariff && (
                  <div className="sa-tennis-summary">
                    Ödenecek Tutar: <strong>{Number(selectedTariff.amount || 0).toFixed(2)} {selectedTariff.currency || 'TRY'}</strong>
                  </div>
                )}

                <div className="sa-action-row sa-tennis-action-row">
                  <button type="button" className="sa-submit-button" onClick={handleContinue} disabled={!tariffs.length}>
                    Ödemeye Geç
                  </button>
                </div>
              </div>
            </div>
          </div>
             ) : (
               <div>
                  <div className="sa-tennis-intro-banner">
                    <div className="sa-intro-content">
                      <h4>Üyelik Yenileme</h4>
                      <p>Mevcut Tenis Akademisi aboneliğinizi yenilemek için TC Kimlik numaranızı girerek sorgulama yapabilirsiniz.</p>
                    </div>
                  </div>
                  <div className="sa-appointment-section sa-tennis-section">
                    <div className="sa-booking-form sa-tennis-form">
                       <h4 className="sa-modern-section-title">
                          <span className="sa-modern-section-line"></span>
                          Üyelik Sorgulama
                        </h4>
                        
                        {!membershipInfo ? (
                          <div className="sa-form-grid" style={{ gridTemplateColumns: 'minmax(200px, 1fr) auto', alignItems: 'end' }}>
                              <div className="sa-form-group">
                                  <label>TC Kimlik Numarası <span className="sa-required">*</span></label>
                                  <input
                                      type="text"
                                      className="sa-form-input"
                                      value={renewalIdentity}
                                      onChange={(e) => setRenewalIdentity(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                      placeholder="11 Haneli TC Kimlik No"
                                  />
                              </div>
                              <div className="sa-form-group" style={{ marginBottom: '16px' }}>
                                  <button type="button" className="sa-submit-button" onClick={handleRenewalSearch} disabled={checkingMembership}>
                                      {checkingMembership ? 'Sorgulanıyor...' : 'Sorgula'}
                                  </button>
                              </div>
                          </div>
                        ) : (
                          <div className="sa-checkout-summary sa-modern-result-summary" style={{ marginTop: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                              <h4 className="sa-modern-result-summary-title" style={{ margin: 0 }}>Abonelik Bilgileri</h4>
                              <button onClick={() => setMembershipInfo(null)} style={{ background: 'none', border: 'none', color: '#0f75bc', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>Farklı TC Sorgula</button>
                            </div>
                            <div className="sa-modern-result-summary-grid">
                              <div className="sa-modern-result-summary-row">
                                <span>Kayıtlı Kişi:</span>
                                <strong>{membershipInfo.maskedName}</strong>
                              </div>
                              <div className="sa-modern-result-summary-row">
                                <span>Mevcut Paket:</span>
                                <strong>{membershipInfo.tariffName}</strong>
                              </div>
                              <div className="sa-modern-result-summary-row">
                                <span>Üyelik Bitiş Tarihi:</span>
                                <strong>
                                  {membershipInfo.membershipEndDate ? new Date(membershipInfo.membershipEndDate).toLocaleDateString('tr-TR') : 'Belirtilmedi'}
                                  {membershipInfo.isExpired ? <span style={{ color: '#ef4444', marginLeft: '8px', fontSize: '13px' }}>(Süresi Doldu)</span> : <span style={{ color: '#10b981', marginLeft: '8px', fontSize: '13px' }}>(Aktif)</span>}
                                </strong>
                              </div>
                              <div className="sa-modern-result-summary-row sa-modern-result-summary-row--total">
                                <span>Yenileme Ücreti (Aylık):</span>
                                <strong>{Number(membershipInfo.amount || 0).toFixed(2)} {membershipInfo.currency}</strong>
                              </div>
                            </div>
                            
                            <div className="sa-action-row" style={{ marginTop: '20px' }}>
                                <button type="button" className="sa-submit-button" onClick={() => setStep(2)}>Ödemeye Geç</button>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
               </div>
             )
                ) : step === 2 ? (
          <div className="sa-appointment-section sa-tennis-section">
            <div className="sa-booking-form sa-tennis-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
              <h4 className="sa-modern-section-title">
                <span className="sa-modern-section-line"></span>
                Ödeme Bilgileri
              </h4>

              <div className="sa-modern-payment-shell sa-tennis-payment-shell">
                <div className="sa-modern-payment-layout">
                  <div className="sa-modern-card-preview sa-modern-card-preview--tennis-app">
                    <span className="sa-modern-card-chip" aria-hidden="true"></span>
                    <div className="sa-modern-card-number">{previewCardNumber}</div>
                    <div className="sa-modern-card-footer">
                      <div>
                        <span>Kart Sahibi</span>
                        <strong>{previewCardHolder}</strong>
                      </div>
                      <div className="sa-modern-card-expiry">
                        <span>SKT</span>
                        <strong>{previewExpiry}</strong>
                      </div>
                    </div>
                    <div className="sa-modern-card-brand">
                      {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#f8fafc" />}
                      {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={28} color="#fed7aa" />}
                      {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={24} color="#dbeafe" />}
                    </div>
                  </div>

                  {/* Agreement Checkboxes (Sadece yeni başvurularda gösterilir) */}
                  {activeTab === 'new' && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div className="sa-terms-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="taTerms" checked={agreements.terms} onChange={(e) => setAgreements({ ...agreements, terms: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }} />
                        <label htmlFor="taTerms" style={{ fontSize: '13px', color: '#475569', cursor: 'pointer', userSelect: 'none', lineHeight: '1.3' }}><span style={{ color: '#0f75bc', fontWeight: '600' }}>Hizmet Şartları</span>'nı okudum ve kabul ediyorum</label>
                      </div>
                      <div className="sa-terms-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="taRefund" checked={agreements.refund} onChange={(e) => setAgreements({ ...agreements, refund: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }} />
                        <label htmlFor="taRefund" style={{ fontSize: '13px', color: '#475569', cursor: 'pointer', userSelect: 'none', lineHeight: '1.3' }}><span style={{ color: '#0f75bc', fontWeight: '600' }}>Para İade Politikası</span>'nı okudum ve kabul ediyorum</label>
                      </div>
                      <div className="sa-terms-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="taContract" checked={agreements.contract} onChange={(e) => setAgreements({ ...agreements, contract: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }} />
                        <label htmlFor="taContract" style={{ fontSize: '13px', color: '#475569', cursor: 'pointer', userSelect: 'none', lineHeight: '1.3' }}><span style={{ color: '#0f75bc', fontWeight: '600' }}>Mesafeli Satış Sözleşmesi</span>'ni okudum ve kabul ediyorum</label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Left side end -> right side start */}
                <div className="sa-modern-payment-form">
                    <div className="sa-form-grid sa-modern-payment-grid">
                      <div className="sa-form-group sa-modern-payment-wide">
                        <label>Kart Numarası</label>
                        <div className="sa-input-with-icon">
                          <input
                            className="sa-form-input"
                            value={paymentData.cardNumber}
                            maxLength={19}
                            inputMode="numeric"
                            placeholder="0000 0000 0000 0000"
                            onChange={(event) => {
                              const raw = event.target.value.replace(/\D/g, '').slice(0, 16);
                              const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
                              setPaymentData((prev) => ({ ...prev, cardNumber: formatted }));
                            }}
                          />
                          <div className="sa-card-brand-icon">
                            {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={24} color="#1a1f71" />}
                            {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={24} color="#f97316" />}
                            {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={22} color="#94a3b8" />}
                          </div>
                        </div>
                        {paymentErrors.cardNumber && <div className="sa-error-message">{paymentErrors.cardNumber}</div>}
                      </div>

                      <div className="sa-form-group sa-modern-payment-wide">
                        <label>Kart Üzerindeki İsim</label>
                        <input
                          className="sa-form-input"
                          name="checkoutCardName"
                          autoComplete="off"
                          value={paymentData.cardName}
                          placeholder="AD SOYAD"
                          onChange={(event) => setPaymentData((prev) => ({ ...prev, cardName: event.target.value.toUpperCase() }))}
                        />
                        {paymentErrors.cardName && <div className="sa-error-message">{paymentErrors.cardName}</div>}
                      </div>

                      <div className="sa-form-group">
                        <label>Ay</label>
                        <select className="sa-form-input" value={paymentData.expiryMonth} onChange={(event) => setPaymentData((prev) => ({ ...prev, expiryMonth: event.target.value }))}>
                          <option value="">Ay</option>
                          {Array.from({ length: 12 }, (_, index) => {
                            const month = String(index + 1).padStart(2, '0');
                            return <option key={month} value={month}>{month}</option>;
                          })}
                        </select>
                      </div>

                      <div className="sa-form-group">
                        <label>Yıl</label>
                        <select className="sa-form-input" value={paymentData.expiryYear} onChange={(event) => setPaymentData((prev) => ({ ...prev, expiryYear: event.target.value }))}>
                          <option value="">Yıl</option>
                          {Array.from({ length: 10 }, (_, index) => {
                            const year = String(new Date().getFullYear() + index);
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>

                      <div className="sa-form-group">
                        <label>CVV</label>
                        <input
                          className="sa-form-input"
                          type="password"
                          name="checkoutCvv"
                          autoComplete="new-password"
                          maxLength={3}
                          inputMode="numeric"
                          value={paymentData.cvv}
                          placeholder="123"
                          onChange={(event) => setPaymentData((prev) => ({ ...prev, cvv: event.target.value.replace(/\D/g, '').slice(0, 3) }))}
                        />
                        {paymentErrors.cvv && <div className="sa-error-message">{paymentErrors.cvv}</div>}
                      </div>
                    </div>
                    {paymentErrors.expiry && <div className="sa-error-message">{paymentErrors.expiry}</div>}
                  </div>
                </div>

              <div className="sa-action-row sa-tennis-action-row sa-tennis-action-row--split">
                <button type="button" className="sa-submit-button" onClick={renderActiveStepButton()} disabled={loading}>
                  {loading ? 'İşleniyor...' : 'Ödemeyi Tamamla'}
                </button>
              </div>
            </div>
          </div>
        ) : paymentOutcome ? (
          <div className={`sa-payment-result sa-tennis-result ${paymentOutcome === 'success' ? 'sa-payment-success' : 'sa-payment-failed'}`}>
            <div className={`sa-result-icon ${paymentOutcome === 'failed' ? 'error' : ''}`}>
              {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            </div>
            <h2>{paymentOutcome === 'success' ? 'Başvuru Alındı' : 'Ödeme Başarısız'}</h2>
            <p className={`sa-result-message ${paymentOutcome === 'failed' ? 'error' : ''}`}>{resultMessage}</p>
                        {paymentOutcome === 'success' && (
              <div className="sa-checkout-summary sa-modern-result-summary">
                <h4 className="sa-modern-result-summary-title">Başvuru Özeti</h4>
                <div className="sa-modern-result-summary-grid">
                  <div className="sa-modern-result-summary-row">
                    <span>Ad Soyad:</span>
                    <strong>{formData.firstName} {formData.lastName}</strong>
                  </div>
                  <div className="sa-modern-result-summary-row">
                    <span>TC Kimlik No:</span>
                    <strong>{formData.identityNumber}</strong>
                  </div>
                  <div className="sa-modern-result-summary-row">
                    <span>Telefon:</span>
                    <strong>{formData.phone}</strong>
                  </div>
                  <div className="sa-modern-result-summary-row">
                    <span>Paket:</span>
                    <strong>{selectedTariff?.tariffName}</strong>
                  </div>
                  {formData.facilityName && (
                    <div className="sa-modern-result-summary-row">
                      <span>Tesis:</span>
                      <strong>{formData.facilityName}</strong>
                    </div>
                  )}
                  <div className="sa-modern-result-summary-row sa-modern-result-summary-row--total">
                    <span>Ödenen Tutar:</span>
                    <strong>{Number(selectedTariff?.amount || 0).toFixed(2)} {selectedTariff?.currency || 'TRY'}</strong>
                  </div>
                </div>
              </div>
            )}
            <div className="sa-action-row sa-tennis-result-actions">
              {paymentOutcome === 'failed' && (
                <button type="button" className="sa-submit-button sa-payment-action" data-variant="positive" onClick={() => { setPaymentOutcome(null); setStep(2); }}>
                  Tekrar Dene
                </button>
              )}
              <button type="button" className="sa-btn-cancel" onClick={() => { resetForm(); setActiveTab(activeTab); }}>
                {activeTab === 'new' ? 'Yeni Başvuru' : 'Başka Sorgula'}
              </button>
            </div>
          </div>
        ) : paymentHtml ? (
          <div className="sa-tennis-iframe-card">
            <div className="sa-tennis-iframe-header">
              <button type="button" className="sa-tennis-iframe-back" onClick={() => { setPaymentHtml(null); setStep(2); }}>
                <FaArrowLeft /> Geri
              </button>
              <strong>{activeTab === 'renewal' && membershipInfo ? Number(membershipInfo.amount || 0).toFixed(2) : Number(selectedTariff?.amount || 0).toFixed(2)} {activeTab === 'renewal' && membershipInfo ? membershipInfo.currency : (selectedTariff?.currency || 'TRY')}</strong>
            </div>
            <iframe
              ref={iframeRef}
              title="Tennis Academy Payment"
              srcDoc={paymentHtml}
              onLoad={handleIframeLoad}
              className="sa-tennis-iframe"
            />
          </div>
        ) : (
          <div className="sa-tennis-loading" style={{ padding: '40px', textAlign: 'center' }}>Ödeme sayfası yükleniyor...</div>
        )}
        </div>
      </div>
    </div>
  );
};

export default TennisAcademyApplicationForm;
